import React, { useEffect, useRef, useState } from "react";
import { StickyNote, X, Send, Trash2 } from "lucide-react";
import { api, useAuth } from "../lib/api";

// Format ISO to "HH:mm" (Türkiye)
function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return "";
    return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

// Group notes by day for date separators
function dayKey(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
}

export default function NotesChatFab() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notes");
      setNotes(Array.isArray(data) ? data : []);
    } catch (e) {
      // silently ignore transient errors
    }
  };

  // Poll while panel is open
  useEffect(() => {
    if (!open) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    load();
    pollRef.current = setInterval(load, 5000);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [open]);

  // Auto-scroll to bottom when notes change
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [notes, open]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      await api.post("/notes", { text: t });
      setText("");
      await load();
    } catch (e) {
      // no-op
    } finally {
      setSending(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu notu silmek istediğinden emin misin?")) return;
    try {
      await api.delete(`/notes/${id}`);
      setNotes((prev) => prev.filter((n) => n.id !== id));
    } catch (e) {
      // no-op
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!user) return null;

  const isAdmin = user.role === "admin";

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          data-testid="notes-fab-btn"
          onClick={() => setOpen(true)}
          title="Notlar"
          aria-label="Notları aç"
          className="fixed z-40 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
          style={{
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 5.5rem)",
            right: "1rem",
            width: 52,
            height: 52,
            backgroundColor: "hsl(var(--primary))",
            color: "white",
            boxShadow: "0 8px 24px rgba(233,116,81,0.35)",
          }}
        >
          <StickyNote className="w-5 h-5" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="notes-panel"
          className="fixed z-40 flex flex-col rounded-2xl overflow-hidden border shadow-2xl bg-white"
          style={{
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 5.5rem)",
            right: "1rem",
            width: "min(calc(100vw - 2rem), 380px)",
            height: "min(calc(100vh - 8rem), 560px)",
            borderColor: "hsl(var(--border))",
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3 border-b"
            style={{ borderColor: "hsl(var(--border))", backgroundColor: "hsl(var(--surface-2))" }}
          >
            <div className="flex items-center gap-2">
              <StickyNote className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} />
              <div className="font-display font-semibold text-sm">Notlar</div>
              <div className="text-[11px]" style={{ color: "hsl(var(--text-2))" }}>
                · grup notu
              </div>
            </div>
            <button
              data-testid="notes-close-btn"
              onClick={() => setOpen(false)}
              aria-label="Kapat"
              className="p-1 rounded-md hover:bg-black/5 transition"
            >
              <X className="w-4 h-4" style={{ color: "hsl(var(--text-2))" }} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="notes-messages"
            className="flex-1 overflow-y-auto px-3 py-3 space-y-2"
            style={{ backgroundColor: "hsl(var(--background))" }}
          >
            {notes.length === 0 && (
              <div className="text-center text-sm py-10" style={{ color: "hsl(var(--text-2))" }}>
                Henüz not yok. İlk notu sen bırak :)
              </div>
            )}
            {notes.map((n, i) => {
              const mine = n.username === user.username;
              const prev = notes[i - 1];
              const showDay = !prev || dayKey(prev.created_at) !== dayKey(n.created_at);
              const canDelete = mine || isAdmin;
              return (
                <React.Fragment key={n.id}>
                  {showDay && (
                    <div className="flex items-center gap-2 py-2">
                      <div className="flex-1 h-px" style={{ backgroundColor: "hsl(var(--border))" }} />
                      <div className="text-[10px] uppercase tracking-wider" style={{ color: "hsl(var(--text-2))" }}>
                        {dayKey(n.created_at)}
                      </div>
                      <div className="flex-1 h-px" style={{ backgroundColor: "hsl(var(--border))" }} />
                    </div>
                  )}
                  <div className={`flex ${mine ? "justify-end" : "justify-start"} group`}>
                    <div
                      className="max-w-[85%] rounded-2xl px-3 py-2 text-sm"
                      style={{
                        backgroundColor: mine ? "hsl(var(--primary))" : "white",
                        color: mine ? "white" : "hsl(var(--text-1))",
                        border: mine ? "none" : "1px solid hsl(var(--border))",
                      }}
                    >
                      {!mine && (
                        <div className="text-[11px] font-semibold mb-0.5" style={{ color: "hsl(var(--primary))" }}>
                          {n.display_name || n.username}
                        </div>
                      )}
                      <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{n.text}</div>
                      <div
                        className="text-[10px] mt-1 flex items-center gap-1 justify-end"
                        style={{ color: mine ? "rgba(255,255,255,0.7)" : "hsl(var(--text-2))" }}
                      >
                        <span>{fmtTime(n.created_at)}</span>
                        {canDelete && (
                          <button
                            data-testid={`notes-delete-${n.id}`}
                            onClick={() => remove(n.id)}
                            className="opacity-0 group-hover:opacity-100 transition p-0.5 rounded hover:bg-black/10"
                            title="Sil"
                            aria-label="Notu sil"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {/* Input */}
          <div
            className="px-3 py-2 border-t flex items-end gap-2"
            style={{ borderColor: "hsl(var(--border))", backgroundColor: "white" }}
          >
            <textarea
              data-testid="notes-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Bir not bırak…  (Enter = gönder)"
              rows={1}
              className="flex-1 resize-none rounded-xl px-3 py-2 text-sm focus:outline-none"
              style={{
                border: "1px solid hsl(var(--border))",
                backgroundColor: "hsl(var(--surface-2))",
                maxHeight: 100,
              }}
            />
            <button
              data-testid="notes-send-btn"
              onClick={send}
              disabled={sending || !text.trim()}
              className="p-2 rounded-xl transition disabled:opacity-40"
              style={{ backgroundColor: "hsl(var(--primary))", color: "white" }}
              aria-label="Gönder"
              title="Gönder"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
